import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useNavigate } from "react-router-dom";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { fmtDateTime } from "@/lib/constants";
import { AlertCircle, Zap, Bell, BellOff } from "lucide-react";
import { subscribeToPush, pushPermission, pushSupported } from "@/lib/push";
import { MyAttendance } from "@/pages/Attendance";

export default function EngineerHome() {
  const [tickets, setTickets] = useState([]);
  const [err, setErr] = useState("");
  const [pushState, setPushState] = useState("prompt"); // prompt|granted|denied|unsupported
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/tickets"); setTickets(r.data); }
      catch (e) { setErr(formatApiError(e)); }
    })();
    (async () => {
      const sup = await pushSupported();
      if (!sup) { setPushState("unsupported"); return; }
      setPushState(pushPermission());
    })();
  }, []);

  const enablePush = async () => {
    const res = await subscribeToPush();
    if (res.ok) setPushState("granted");
    else setPushState(res.reason === "denied" ? "denied" : pushPermission());
  };

  const active = tickets.filter((t) => !["completed", "closed", "cancelled"].includes(t.status));

  return (
    <div className="px-4 py-4">
      <header className="mb-4">
        <p className="label-eyebrow text-primary">My jobs</p>
        <h1 className="mt-1 font-heading text-2xl font-bold tracking-tight">{active.length} active</h1>
        <p className="mt-1 text-xs text-muted-foreground">Tap a job to open, update status and log parts.</p>
      </header>

      <MyAttendance compact />

      {pushState === "prompt" && (
        <button
          data-testid="engineer-enable-push"
          onClick={enablePush}
          className="mb-3 flex w-full items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs font-semibold text-primary"
        >
          <Bell className="h-3.5 w-3.5" /> Enable push notifications for new job alerts
        </button>
      )}
      {pushState === "denied" && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs text-orange-400">
          <BellOff className="h-3.5 w-3.5" /> Notifications blocked. Enable them in your browser settings to receive job alerts.
        </div>
      )}
      {pushState === "granted" && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-400">
          <Bell className="h-3.5 w-3.5" /> Push notifications on — you&apos;ll get pinged when a new job is assigned.
        </div>
      )}

      {err && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="h-4 w-4" /> {err}
        </div>
      )}

      <div className="space-y-2">
        {active.map((t) => (
          <button
            key={t.id}
            data-testid={`engineer-job-${t.ticket_no}`}
            onClick={() => navigate(`/engineer/job/${t.id}`)}
            className="sharp-card w-full p-4 text-left transition-colors hover:border-primary/50"
          >
            <div className="mb-1 flex items-center justify-between">
              <span className="font-mono text-[11px] text-muted-foreground">{t.ticket_no}</span>
              <PriorityBadge priority={t.priority} />
            </div>
            <p className="line-clamp-2 text-sm font-medium">{t.complaint}</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="truncate text-[11px] text-muted-foreground">
                {t.customer?.company_name} · {t.customer?.address?.slice(0, 40) || "—"}
              </p>
              <StatusBadge status={t.status} />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">{fmtDateTime(t.created_at)}</p>
          </button>
        ))}
        {active.length === 0 && (
          <div className="sharp-card p-10 text-center">
            <Zap className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">All caught up.</p>
            <p className="mt-1 text-xs text-muted-foreground">No active jobs assigned right now.</p>
          </div>
        )}
      </div>
    </div>
  );
}
